import { Box, Button } from "@chakra-ui/react";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useGetOneUser, useUpdateOneUser } from "shared/api/requests/users";
import Card from "shared/components/horizon-ui/card/Card";
import { HSeparator } from "shared/components/horizon-ui/separator/Separator";
import Link from "shared/components/Link";
import Loader from "shared/components/Loader";
import {
  getUpdateUserValues,
  UpdateUserFormT,
  updateUserResolver,
} from "shared/form-values/users";
import useGetColors from "shared/hooks/useGetColors";
import { PathsE, PathsParams } from "shared/routes";
import PageTitle from "../admin/PageTitle";
import UserForm from "./Form";
import { fillFormErrors } from "../../utils/devUtils";

const Update = () => {
  const { t } = useTranslation();
  const { userId } = useParams<PathsParams[PathsE.UPDATE_USER]>();
  const { mutate, isPending: isLoadingUpdate } = useUpdateOneUser({
    pathParams: { userId },
    params: undefined,
  });
  const { data, isLoading } = useGetOneUser({
    pathParams: { userId },
    params: undefined,
  });

  const { gray800, white, gray100, brand500, brand400 } = useGetColors();

  const methods = useForm<UpdateUserFormT>({
    defaultValues: getUpdateUserValues(data),
    resolver: updateUserResolver,
    mode: "onChange",
  });
  const {
    formState: { isValid },
    trigger,
  } = methods;

  useEffect(() => {
    trigger();
  }, []);

  useEffect(() => {
    methods.reset({
      ...data,
      resources: data?.resources?.map((r) => ({
        resourceId: r.resource.id,
        resourceType: r.resource.type,
      })),
    });
  }, [data]);

  const onSubmit: SubmitHandler<UpdateUserFormT> = (values) => {
    mutate(values, {
      onError: (error) => fillFormErrors(methods.setError, error),
    });
  };

  const breadcrumbs = [
    {
      path: PathsE.HOME,
      text: t("general.home"),
    },
    {
      path: PathsE.USERS,
      text: t("users.users"),
    },

  ];

  if (isLoading) return <Loader />;

  return (
    <Box>
      <PageTitle
        title={t("users.editUser")}
        breadcrumbs={breadcrumbs}
        backUrl={{
          path: PathsE.USERS,
        }}
      />
      <Card mb="20px" borderRadius="20px" px="30px">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <UserForm defaultResources={data?.resources} />
            <HSeparator my="20px" mx="-20px" width="calc(100% + 40px)" />
            <Link
              path={PathsE.USERS}
              pathsParams={{ userId }}
            >
              <Button
                color={gray800}
                bgColor={gray100}
                borderRadius="8px"
                w="100px"
              >
                {t("buttons.cancel")}
              </Button>
            </Link>
            <Button
              color={white}
              _hover={{
                bgColor: brand400,
              }}
              bgColor={brand500}
              borderRadius="8px"
              ml="20px"
              isLoading={isLoadingUpdate}
              w="100px"
              type="submit"
              isDisabled={!isValid}
            >
              {t("buttons.edit")}
            </Button>
          </form>
        </FormProvider>
      </Card>
    </Box>
  );
};

export default Update;
